import React from 'react';
import { useTranslation } from 'react-i18next';
import { ImportFlowState, ActualServer, ConversionResult, ImportResult, NewServerForm } from '../../types';

interface ImportModalProps {
  isVisible: boolean;
  onClose: () => void;
  importFlow: ImportFlowState;
  isLoading: boolean;
  error: string | null;
  savedServers: ActualServer[];
  result: ConversionResult | null;
  selectedTransactionCount: number;
  conversionCurrency?: string;
  budgetCurrency?: string | null;
  isImporting: boolean;
  importResult: ImportResult | null;
  overspendLoading?: boolean;
  hasOverspending?: boolean;
  onFixOverspending?: () => void;
  newServerForm: NewServerForm;
  onNewServerFormChange: (form: Partial<NewServerForm>) => void;
  onServerSelection: (server: ActualServer) => void;
  onCreateServer: () => void;
  onBudgetSelection: (budgetId: string, budgetPassword?: string) => void;
  onImportFlowChange: (changes: Partial<ImportFlowState>) => void;
  onExecuteImport: () => void;
  onRemoveServer: (serverId: string) => void;
  onRemoveBudgetPassword: (serverId: string, budgetId: string) => void;
  onShowServerManagement: () => void;
}

const ImportModal: React.FC<ImportModalProps> = ({
  isVisible,
  onClose,
  importFlow,
  isLoading,
  error,
  savedServers,
  result,
  selectedTransactionCount,
  conversionCurrency,
  budgetCurrency,
  isImporting,
  importResult,
  overspendLoading = false,
  hasOverspending = false,
  onFixOverspending,
  newServerForm,
  onNewServerFormChange,
  onServerSelection,
  onCreateServer,
  onBudgetSelection,
  onImportFlowChange,
  onExecuteImport,
  onRemoveServer,
  onRemoveBudgetPassword,
  onShowServerManagement
}) => {
  const { t } = useTranslation();
  const [showAccountPicker, setShowAccountPicker] = React.useState(false);
  const [currencyMismatchConfirmed, setCurrencyMismatchConfirmed] = React.useState(false);

  const hasCurrencyMismatch = Boolean(
    budgetCurrency &&
    conversionCurrency &&
    conversionCurrency !== budgetCurrency
  );

  React.useEffect(() => {
    if (!isVisible) {
      setShowAccountPicker(false);
      setCurrencyMismatchConfirmed(false);
    }
  }, [isVisible]);
  
  if (!isVisible) return null;

  const selectedAccount = importFlow.accounts.find(
    (account) => account.id === importFlow.selectedAccountId
  );

  const handleImportClick = () => {
    if (hasCurrencyMismatch && !currencyMismatchConfirmed) {
      setCurrencyMismatchConfirmed(true);
      return;
    }

    onExecuteImport();
  };

  const getModalTitle = () => {
    switch (importFlow.step) {
      case 'accounts': return t('loadingAccounts', { ns: 'import' });
      case 'ready':
        return importFlow.accountPreselected
          ? t('confirmImport', { ns: 'import' })
          : t('selectAccount', { ns: 'import' });
      case 'no-default-budget': return t('defaultBudgetRequired', { ns: 'import' });
      default: return t('title', { ns: 'import' });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{getModalTitle()}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          {savedServers.length === 0 ? (
            <div className="no-servers-message">
              <h4>{t('connectServer', { ns: 'import' })}</h4>
              <p>{t('directImportDescription', { ns: 'import' })}</p>
              <button 
                onClick={() => {
                  onShowServerManagement();
                  onClose();
                }}
                className="connect-server-button"
              >
                {t('setupServerConnection', { ns: 'import' })}
              </button>
              <button 
                onClick={onClose}
                className="skip-button secondary"
              >
                {t('cancel', { ns: 'import' })}
              </button>
            </div>
          ) : (
            <>
              {importFlow.step === 'servers' && (
                <div className="server-selection">
                  <h4>{t('selectActualServer', { ns: 'import' })}</h4>
                  
                  {savedServers.length > 0 && (
                    <div className="saved-servers">
                      <h5>{t('savedServers', { ns: 'import' })}</h5>
                      {savedServers.map(server => (
                        <div key={server.id} className="server-item">
                          <div className="server-info">
                            <strong>{server.name}</strong>
                            <span>{server.url}</span>
                          </div>
                          <div className="server-actions">
                            <button 
                              onClick={() => onServerSelection(server)}
                              className="select-button"
                              disabled={isLoading}
                            >
                              {t('select', { ns: 'import' })}
                            </button>
                            {server.budgetPasswords && Object.keys(server.budgetPasswords).length > 0 && (
                              <button 
                                onClick={() => {
                                  Object.keys(server.budgetPasswords!).forEach(budgetId => 
                                    onRemoveBudgetPassword(server.id, budgetId)
                                  );
                                }}
                                className="remove-button"
                                disabled={isLoading}
                                style={{fontSize: '0.7rem', padding: '0.25rem 0.5rem'}}
                              >
                                {t('clearPasswords', { ns: 'import' })}
                              </button>
                            )}
                            <button 
                              onClick={() => onRemoveServer(server.id)}
                              className="remove-button"
                              disabled={isLoading}
                            >
                              {t('remove', { ns: 'import' })}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="add-server">
                    <h5>{t('addNewServer', { ns: 'import' })}</h5>
                    <div className="form-group">
                      <label>{t('serverName', { ns: 'import' })}</label>
                      <input
                        type="text"
                        value={newServerForm.name}
                        onChange={(e) => onNewServerFormChange({ name: e.target.value })}
                        placeholder={t('serverNamePlaceholder', { ns: 'import' })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('serverUrl', { ns: 'import' })}</label>
                      <input
                        type="url"
                        value={newServerForm.url}
                        onChange={(e) => onNewServerFormChange({ url: e.target.value })}
                        placeholder={t('serverUrlPlaceholder', { ns: 'import' })}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('password', { ns: 'import' })}</label>
                      <input
                        type="password"
                        value={newServerForm.password}
                        onChange={(e) => onNewServerFormChange({ password: e.target.value })}
                        placeholder={t('passwordPlaceholder', { ns: 'import' })}
                      />
                      <p className="password-security-notice">{t('passwordSecurityNotice', { ns: 'common' })}</p>
                    </div>
                    <button 
                      onClick={onCreateServer}
                      className="create-server-button"
                      disabled={isLoading}
                    >
                      {isLoading ? t('connecting', { ns: 'import' }) : t('connectAndSaveServer', { ns: 'import' })}
                    </button>
                  </div>
                </div>
              )}

              {importFlow.step === 'budgets' && (
                <div className="budget-selection">
                  <h4>{t('selectBudget', { ns: 'import' })}</h4>
                  <p>{t('server', { ns: 'import' })} <strong>{importFlow.selectedServer?.name}</strong></p>
                  
                  <div className="budget-list">
                    {importFlow.budgets.map(budget => (
                      <div 
                        key={budget.id} 
                        className={`budget-item ${importFlow.selectedBudgetId === budget.id ? 'selected' : ''}`}
                        onClick={() => onImportFlowChange({ selectedBudgetId: budget.id })}
                      >
                        <span>
                          {budget.name}
                          {importFlow.selectedServer?.budgetPasswords?.[budget.id] && (
                            <small style={{marginLeft: '8px', color: '#4CAF50'}}>{t('savedPassword', { ns: 'import' })}</small>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="step-actions">
                    <button onClick={() => onImportFlowChange({ step: 'servers' })}>
                      {t('back', { ns: 'import' })}
                    </button>
                    <button 
                      onClick={() => importFlow.selectedBudgetId && onBudgetSelection(importFlow.selectedBudgetId)}
                      disabled={!importFlow.selectedBudgetId || isLoading}
                    >
                      {isLoading ? t('loading', { ns: 'import' }) : t('next', { ns: 'import' })}
                    </button>
                  </div>
                </div>
              )}

              {importFlow.step === 'budget-password' && (
                <div className="budget-password">
                  <h4>{t('budgetPasswordRequired', { ns: 'import' })}</h4>
                  <p>{t('budget', { ns: 'import' })} <strong>{importFlow.budgets.find(b => b.id === importFlow.selectedBudgetId)?.name}</strong></p>
                  <p>{t('budgetEncryptedDescription', { ns: 'import' })}</p>
                  {importFlow.selectedServer?.budgetPasswords?.[importFlow.selectedBudgetId!] && (
                    <p style={{color: '#4CAF50', fontStyle: 'italic'}}>
                      {t('savedPasswordDidntWork', { ns: 'import' })}
                    </p>
                  )}
                  
                  <div className="form-group">
                    <label>{t('budgetPassword', { ns: 'import' })}</label>
                    <input
                      type="password"
                      value={importFlow.budgetPassword || ''}
                      onChange={(e) => onImportFlowChange({ budgetPassword: e.target.value })}
                      placeholder={t('budgetPasswordPlaceholder', { ns: 'import' })}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && importFlow.selectedBudgetId && importFlow.budgetPassword) {
                          onBudgetSelection(importFlow.selectedBudgetId, importFlow.budgetPassword);
                        }
                      }}
                    />
                    <p className="password-security-notice">{t('passwordSecurityNotice', { ns: 'common' })}</p>
                  </div>
                  
                  <div className="step-actions">
                    <button onClick={() => onImportFlowChange({ step: 'budgets', needsBudgetPassword: false, budgetPassword: '' })}>
                      {t('back', { ns: 'import' })}
                    </button>
                    <button 
                      onClick={() => importFlow.selectedBudgetId && importFlow.budgetPassword && onBudgetSelection(importFlow.selectedBudgetId, importFlow.budgetPassword)}
                      disabled={!importFlow.budgetPassword || isLoading}
                    >
                      {isLoading ? t('unlocking', { ns: 'import' }) : t('unlockBudget', { ns: 'import' })}
                    </button>
                  </div>
                </div>
              )}

              {importFlow.step === 'accounts' && (
                <div className="loading-accounts">
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <h4>{t('loadingAccountsTitle', { ns: 'import' })}</h4>
                    <p>{t('server', { ns: 'import' })} <strong>{importFlow.selectedServer?.name}</strong></p>
                    <p>{t('budget', { ns: 'import' })} <strong>{importFlow.selectedServer?.defaultBudgetName}</strong></p>
                    <p>{t('loadingAccountsDescription', { ns: 'import' })}</p>
                  </div>
                </div>
              )}

              {importFlow.step === 'ready' && (
                <div className="account-selection">
                  <h4>
                    {importFlow.accountPreselected && !showAccountPicker
                      ? t('confirmImportToAccount', {
                          ns: 'import',
                          accountName: selectedAccount?.name || ''
                        })
                      : t('selectAccountForImport', { ns: 'import' })}
                  </h4>
                  <div className="import-config-summary">
                    <p><strong>{t('server', { ns: 'import' })}</strong> {importFlow.selectedServer?.name}</p>
                    <p><strong>{t('budget', { ns: 'import' })}</strong> {importFlow.selectedServer?.defaultBudgetName}</p>
                    {importFlow.accountPreselected && selectedAccount && !showAccountPicker && (
                      <p><strong>{t('account', { ns: 'import' })}</strong> {selectedAccount.name}</p>
                    )}
                    <p><strong>{t('transactionsToImport', { ns: 'import' })}</strong> {selectedTransactionCount}</p>
                    {conversionCurrency && (
                      <p><strong>{t('convertTo', { ns: 'import' })}</strong> {conversionCurrency}</p>
                    )}
                    {budgetCurrency && (
                      <p><strong>{t('budgetCurrency', { ns: 'import' })}</strong> {budgetCurrency}</p>
                    )}
                    {hasCurrencyMismatch && (
                      <p className="currency-mismatch-warning" role="alert">
                        {t('importCurrencyMismatchWarning', {
                          ns: 'import',
                          convertCurrency: conversionCurrency,
                          budgetCurrency,
                        })}
                      </p>
                    )}
                    {importFlow.isRefreshingAccounts && (
                      <div className="refresh-indicator">
                        <small style={{ 
                          color: '#666', 
                          fontSize: '0.8rem', 
                          fontStyle: 'italic',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '8px', 
                            height: '8px', 
                            border: '1px solid #666', 
                            borderTop: '1px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></span>
                          {t('refreshingAccounts', { ns: 'import' })}
                        </small>
                      </div>
                    )}
                  </div>

                  {importFlow.accountPreselected && !showAccountPicker ? (
                    <button
                      type="button"
                      onClick={() => setShowAccountPicker(true)}
                      className="change-account-button"
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#1976d2',
                        cursor: 'pointer',
                        padding: '0.5rem 0',
                        fontSize: '0.9rem',
                        textDecoration: 'underline'
                      }}
                    >
                      {t('changeAccount', { ns: 'import' })}
                    </button>
                  ) : (
                    <div className="account-list">
                      {importFlow.accounts.map(account => (
                        <div 
                          key={account.id} 
                          className={`account-item ${importFlow.selectedAccountId === account.id ? 'selected' : ''}`}
                          onClick={() => onImportFlowChange({ selectedAccountId: account.id, accountPreselected: false })}
                        >
                          <span>{account.name}</span>
                          <small>{account.type}</small>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {hasCurrencyMismatch && currencyMismatchConfirmed && !importResult && (
                    <p className="currency-mismatch-confirm" role="alert">
                      {t('importCurrencyMismatchConfirm', {
                        ns: 'import',
                        convertCurrency: conversionCurrency,
                        budgetCurrency,
                      })}
                    </p>
                  )}

                  {!importResult && (
                    <div className="step-actions">
                      <button 
                        onClick={onClose}
                        className="cancel-button"
                      >
                        {t('cancel', { ns: 'import' })}
                      </button>
                      <button 
                        onClick={handleImportClick}
                        disabled={!importFlow.selectedAccountId || isImporting || selectedTransactionCount === 0}
                        className="import-execute-button"
                      >
                        {isImporting ? (
                          <>
                            <span className="spinner" style={{ 
                              display: 'inline-block', 
                              width: '12px', 
                              height: '12px', 
                              border: '2px solid #fff', 
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              marginRight: '8px'
                            }}></span>
                            {t('importing', { ns: 'import' })}
                          </>
                        ) : hasCurrencyMismatch && !currencyMismatchConfirmed ? (
                          t('reviewCurrencyMismatch', { ns: 'import' })
                        ) : (
                          t('importTransactions', { ns: 'import' })
                        )}
                      </button>
                    </div>
                  )}

                  {importResult && (
                    <div className="import-result">
                      <h5>{t('importComplete', { ns: 'import' })}</h5>
                      <div className="import-info">
                        <p><strong>{t('transactionsImported', { ns: 'import' })}</strong> {importResult.imported}</p>
                        {importResult.errors.length > 0 && (
                          <div className="import-errors">
                            <p><strong>{t('warningsErrors', { ns: 'import' })}</strong></p>
                            <ul>
                              {importResult.errors.map((error, index) => (
                                <li key={index}>{error}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      <div className="step-actions">
                        {overspendLoading && (
                          <p className="overspend-checking">{t('checkingOverspending', { ns: 'import' })}</p>
                        )}
                        {hasOverspending && onFixOverspending && (
                          <button
                            type="button"
                            onClick={onFixOverspending}
                            className="review-account-button primary"
                            style={{
                              padding: '12px 24px',
                              backgroundColor: '#1976d2',
                              color: 'white',
                              borderRadius: '6px',
                              fontWeight: 'bold',
                              marginRight: '12px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            {t('fixOverspending', { ns: 'import' })}
                          </button>
                        )}
                        {importFlow.selectedAccountId && importFlow.selectedServer && (
                          <a 
                            href={`${importFlow.selectedServer.url}/accounts/${importFlow.selectedAccountId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="review-account-button primary"
                            style={{
                              textDecoration: 'none',
                              display: 'inline-block',
                              padding: '12px 24px',
                              backgroundColor: '#4CAF50',
                              color: 'white',
                              borderRadius: '6px',
                              fontWeight: 'bold',
                              marginRight: '12px',
                              border: 'none',
                              cursor: 'pointer'
                            }}
                          >
                            {t('reviewAndCategorize', { ns: 'import' })}
                          </a>
                        )}
                        <button 
                          onClick={onClose}
                          className="close-modal-button secondary"
                          style={{
                            padding: '12px 24px',
                            backgroundColor: '#f5f5f5',
                            color: '#666',
                            border: '1px solid #ddd',
                            borderRadius: '6px'
                          }}
                        >
                          {t('close', { ns: 'import' })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {importFlow.step === 'no-default-budget' && (
                <div className="no-default-budget">
                  <h4>{t('defaultBudgetRequiredTitle', { ns: 'import' })}</h4>
                  <p>{t('defaultBudgetRequiredDescription', { ns: 'import' })}</p>
                  <p><strong>{t('server', { ns: 'import' })}</strong> {importFlow.selectedServer?.name}</p>
                  
                  <div className="action-options">
                    <button 
                      onClick={() => {
                        onShowServerManagement();
                        onClose();
                      }}
                      className="setup-button primary"
                    >
                      {t('configureDefaultBudget', { ns: 'import' })}
                    </button>
                    <button 
                      onClick={onClose}
                      className="skip-button secondary"
                    >
                      {t('cancel', { ns: 'import' })}
                    </button>
                  </div>
                </div>
              )}

              {error && (
                <div className="error">
                  <h4>{t('error', { ns: 'import' })}</h4>
                  <p>{error}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportModal; 